// This is the client's API it uses for interacting with the `server`.

import type { CoreUserRoleType, CourseMonitoringData, PageType } from "../components/server-types";

import type {
  ClassSessionSummaryType,
  CourseAssessmentSummaryData,
  CourseAssessmentSummaryParams,
  CourseEnrollmentData,
  CourseGradesData,
  CourseOutcomeSummaryData,
  FullAssessmentData,
  InstructorAssessmentDataForCourseType,
  InviteStatus,
  PageAssessmentStatusType,
  PageAssessmentSummaryData,
  ProgressOverviewData,
  StudentAssessmentDataForCourseType,
  StudentOutcomeCompetencyData,
  SystemCheckResultsData,
  UserEngagementDataType,
  UserInviteStatusType,
} from "./api-data-types";
import { SERVER_API_PATH, reportApiError, serverApi } from "./api-server-actions";
import { createCached } from "./cache";

import { getOrgIdFromCookie } from "utils/auth";

export async function serverGetPageAssessmentStatuses({
  pageIds,
  courseId,
}: {
  pageIds?: string[];
  courseId?: string;
}): Promise<PageAssessmentStatusType> {
  const params: { page_ids?: string[]; course_id?: string } = {};
  if (pageIds) {
    params.page_ids = pageIds;
  }

  if (courseId) {
    params.course_id = courseId;
  }

  try {
    const response = await serverApi.post<PageAssessmentStatusType>(
      `${SERVER_API_PATH}/material/pages/assessment_status/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverPageAssessmentStatus", error);
    throw error;
  }
}

// Type for the server response format
type UserInviteStatusResponseType = Array<{
  user_id: string;
  invite_status: string;
}>;

export async function serverGetUserInviteStatuses(): Promise<UserInviteStatusType> {
  try {
    const response = await serverApi.get<UserInviteStatusResponseType>(
      `${SERVER_API_PATH}/orgs/org_users/invite_status/`,
    );

    // Convert array of objects to Record<string, InviteStatus>
    const statusesRecord: UserInviteStatusType = {};
    response.data.forEach((item) => {
      statusesRecord[item.user_id] = item.invite_status as InviteStatus;
    });

    return statusesRecord;
  } catch (error) {
    reportApiError("serverUserInviteStatus", error);
    throw error;
  }
}

export async function serverGetCourseAssessmentSummary(
  params: CourseAssessmentSummaryParams,
): Promise<CourseAssessmentSummaryData[]> {
  try {
    const response = await serverApi.post<CourseAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/course_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetCourseAssessmentSummary", error);
    throw error;
  }
}

export async function serverGetPageAssessmentSummary(
  params: CourseAssessmentSummaryParams,
): Promise<PageAssessmentSummaryData[]> {
  try {
    const response = await serverApi.post<PageAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/page_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetPageAssessmentSummary", error);
    throw error;
  }
}

export async function serverGetFullAssessmentData(
  params: CourseAssessmentSummaryParams,
): Promise<FullAssessmentData[]> {
  try {
    const response = await serverApi.post<FullAssessmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/full_report/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetFullAssessmentData", error);
    throw error;
  }
}

export async function serverGetCourseEnrollmentSummary(params: {
  term_id: string;
  course_code_id?: string;
}): Promise<CourseEnrollmentData[]> {
  try {
    const response = await serverApi.post<CourseEnrollmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/course_enrollment_summary/`,
      params,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetCourseEnrollmentSummary", error);
    throw error;
  }
}

export async function serverGetCourseOutcomeSummary(
  termId: string,
): Promise<CourseOutcomeSummaryData[]> {
  try {
    const response = await serverApi.post<CourseOutcomeSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/course_outcome_summary/`,
      { term_id: termId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetCourseOutcomeSummary", error);
    throw error;
  }
}

export async function serverGetStudentOutcomeCompetency(
  termId?: string,
): Promise<StudentOutcomeCompetencyData[]> {
  try {
    const response = await serverApi.post<StudentOutcomeCompetencyData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/student_outcome_competency/`,
      { term_id: termId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetStudentOutcomeCompetency", error);
    throw error;
  }
}

export async function serverGetCourseGrades(
  termId: string,
  courseCodeId: string | undefined,
): Promise<CourseGradesData[]> {
  try {
    const response = await serverApi.post<CourseGradesData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/course_grades/`,
      { term_id: termId, course_code_id: courseCodeId },
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetCourseGrades", error);
    throw error;
  }
}

// Returns engagement data for all 3 date ranges: 7 days, 30 days, and -1 (all time)
export const serverGetEngagementData = createCached(
  async (courseId: string): Promise<Record<number, Record<string, UserEngagementDataType>>> => {
    try {
      const response = await serverApi.post<Record<number, Record<string, UserEngagementDataType>>>(
        `${SERVER_API_PATH}/material/courses/${courseId}/engagement/`,
        {},
      );

      return response.data;
    } catch (error) {
      reportApiError("serverGetEngagementData", error);
      throw error;
    }
  },
  60 * 10, // 10 minutes
);

export type StudentSessionMetric = {
  user_id: string;
  class_session_id: string;
  talk_time_seconds: number;
};

export const serverGetStudentSessionMetrics = createCached(
  async (courseId: string): Promise<StudentSessionMetric[]> => {
    try {
      const response = await serverApi.get<StudentSessionMetric[]>(
        `${SERVER_API_PATH}/metrics/events/student-session-metrics/`,
        {
          params: {
            course_id: courseId,
          },
        },
      );

      return response.data;
    } catch (error) {
      reportApiError("serverGetStudentSessionMetrics", error);
      throw error;
    }
  },
  60 * 10, // 10 minutes
);

export async function serverGetServerTime(): Promise<{ server_time: number }> {
  try {
    const response = await serverApi.get<{ server_time: number }>("/api/v1/basebox/server-time/", {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    });
    return response.data;
  } catch (error) {
    reportApiError("serverGetServerTime", error);
    throw error;
  }
}

export async function serverGetProgressOverviewData(): Promise<ProgressOverviewData> {
  try {
    const response = await serverApi.get<ProgressOverviewData>(
      `${SERVER_API_PATH}/material/outcome_assessments/progress_overview/`,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetProgressOverviewData", error);
    throw error;
  }
}

// This should only be used for the monitoring dashboard. It retrieves nested course data across all orgs.
export async function serverGetCoursesForMonitoring(): Promise<CourseMonitoringData[]> {
  try {
    const response = await serverApi.get<CourseMonitoringData[]>(
      `${SERVER_API_PATH}/material/courses/for_monitoring/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetCoursesForMonitoring", error);
    throw error;
  }
}

export const getClassSessionSummaries = async (
  courseId: string,
): Promise<ClassSessionSummaryType[]> => {
  try {
    const response = await serverApi.get<ClassSessionSummaryType[]>(
      `${SERVER_API_PATH}/metrics/events/class-session-summaries/`,
      {
        params: {
          course_id: courseId,
        },
      },
    );
    return response.data;
  } catch (error) {
    reportApiError("getClassSessionSummaries", error);
    throw error;
  }
};

export async function serverGetInstructorAssessments() {
  const orgId = getOrgIdFromCookie();
  try {
    const response = await serverApi.get<InstructorAssessmentDataForCourseType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/instructor_assessments/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetInstructorAssessments", error);
    throw error;
  }
}

export async function serverGetStudentAssessments() {
  const orgId = getOrgIdFromCookie();
  try {
    const response = await serverApi.get<StudentAssessmentDataForCourseType[]>(
      `${SERVER_API_PATH}/orgs/orgs/${orgId}/student_assessments/`,
    );
    return response.data;
  } catch (error) {
    reportApiError("serverGetStudentAssessments", error);
    throw error;
  }
}

export async function serverGetHomePlannerPages(userRole: CoreUserRoleType): Promise<PageType[]> {
  try {
    const response = await serverApi.get<PageType[]>(
      `${SERVER_API_PATH}/material/pages/home_planner/?user_role=${userRole}`,
    );

    return response.data;
  } catch (error) {
    reportApiError("serverGetHomePlannerPages", error);
    throw error;
  }
}

export async function serverGetSystemCheckResults(
  start_date: string,
): Promise<SystemCheckResultsData[]> {
  try {
    const response = await serverApi.get(`${SERVER_API_PATH}/system_check/check_results/`, {
      params: { created_at__gte: start_date },
    });
    return response.data;
  } catch (error) {
    reportApiError("serverGetSystemCheckResults", error);
    throw error;
  }
}
